/**
 * ALOHANOVA UNIVERSE - Contact Page
 * Copyright © 2025 Malcolm KH Lee III. All Rights Reserved.
 */

import React, { useState } from 'react';
import { Mail, MessageCircle, Send, MapPin, Phone } from 'lucide-react';
import Enhanced8KGalaxyBackground from '../components/Enhanced8KGalaxyBackground';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 3000);
  };

  return (
    <div className="min-h-screen bg-gray-900">
      <Enhanced8KGalaxyBackground quality="medium" />
      
      <div className="relative z-10 container mx-auto px-6 py-20 max-w-4xl">
        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold text-white mb-4">Contact Us</h1>
          <p className="text-xl text-gray-300">Get in touch with the Alohanova team</p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Contact Form */}
          <div className="bg-gray-800/70 backdrop-blur-xl rounded-2xl p-8 border border-purple-500/30">
            <h2 className="text-2xl font-bold text-white mb-6">Send a Message</h2>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-gray-300 mb-2">Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full px-4 py-3 bg-gray-900/50 border border-gray-700 rounded-lg text-white"
                  required
                />
              </div>
              
              <div>
                <label className="block text-gray-300 mb-2">Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="w-full px-4 py-3 bg-gray-900/50 border border-gray-700 rounded-lg text-white"
                  required
                />
              </div>
              
              <div>
                <label className="block text-gray-300 mb-2">Subject</label>
                <input
                  type="text"
                  value={formData.subject}
                  onChange={(e) => setFormData({...formData, subject: e.target.value})}
                  className="w-full px-4 py-3 bg-gray-900/50 border border-gray-700 rounded-lg text-white"
                  required
                />
              </div>
              
              <div>
                <label className="block text-gray-300 mb-2">Message</label>
                <textarea
                  value={formData.message}
                  onChange={(e) => setFormData({...formData, message: e.target.value})}
                  className="w-full px-4 py-3 bg-gray-900/50 border border-gray-700 rounded-lg text-white"
                  rows="5"
                  required
                />
              </div>
              
              <button
                type="submit"
                className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white font-bold rounded-lg transition"
              >
                <Send className="w-5 h-5" />
                Send Message
              </button>
              
              {submitted && (
                <div className="bg-green-500/20 border border-green-500 text-green-300 px-4 py-3 rounded-lg">
                  ✓ Message sent! We'll respond within 24 hours.
                </div>
              )}
            </form>
          </div>

          {/* Contact Info */}
          <div className="space-y-6">
            <div className="bg-gray-800/70 backdrop-blur-xl rounded-2xl p-6 border border-blue-500/30">
              <div className="flex items-center gap-3 mb-4">
                <Mail className="w-6 h-6 text-blue-400" />
                <h3 className="text-xl font-bold text-white">Email</h3>
              </div>
              <a href="mailto:malcolmlee3@gmail.com" className="text-blue-400 hover:text-blue-300">
                malcolmlee3@gmail.com
              </a>
            </div>
            
            <div className="bg-gray-800/70 backdrop-blur-xl rounded-2xl p-6 border border-purple-500/30">
              <div className="flex items-center gap-3 mb-4">
                <MapPin className="w-6 h-6 text-purple-400" />
                <h3 className="text-xl font-bold text-white">Location</h3>
              </div>
              <p className="text-gray-300">Hawaii, United States</p>
              <p className="text-gray-400 text-sm mt-2">Serving the world 🌍</p>
            </div>
            
            <div className="bg-gray-800/70 backdrop-blur-xl rounded-2xl p-6 border border-green-500/30">
              <div className="flex items-center gap-3 mb-4">
                <MessageCircle className="w-6 h-6 text-green-400" />
                <h3 className="text-xl font-bold text-white">Business Hours</h3>
              </div>
              <p className="text-gray-300">24/7 AI Support Available</p>
              <p className="text-gray-400 text-sm mt-2">Human support: Mon-Fri 9AM-5PM HST</p>
            </div>
            
            <div className="bg-gray-800/70 backdrop-blur-xl rounded-2xl p-6 border border-yellow-500/30">
              <h3 className="text-xl font-bold text-white mb-4">Social Media</h3>
              <div className="space-y-2 text-gray-300">
                <p>Twitter/X: @AlohanovaAI (creating)</p>
                <p>Instagram: @alohanova.universe (creating)</p>
                <p>LinkedIn: Alohanova Universe (creating)</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;